import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for lazy loading images using Intersection Observer
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (0-1)
 * @param {string} options.rootMargin - Root margin for intersection
 * @param {boolean} options.triggerOnce - Only trigger once when visible
 * @returns {Object} - { isVisible, ref, hasIntersected }
 */
export const useLazyLoading = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '50px',
    triggerOnce = true
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const ref = useRef(null);

  const handleIntersection = useCallback((entries) => {
    const [entry] = entries;
    
    if (entry.isIntersecting) {
      setIsVisible(true);
      if (triggerOnce) {
        setHasIntersected(true);
      }
    } else if (!triggerOnce) {
      setIsVisible(false);
    }
  }, [triggerOnce]);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      // Fallback for older browsers
      setIsVisible(true);
      setHasIntersected(true);
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [handleIntersection, threshold, rootMargin]);

  return { isVisible, ref, hasIntersected };
};

/**
 * Hook for preloading images with priority
 * @param {Array} imageUrls - Array of image URLs to preload
 * @param {number} maxConcurrent - Maximum concurrent image loads
 * @returns {Object} - { loadedImages, loadingImages, preloadImage }
 */
export const useImagePreloader = (imageUrls = [], maxConcurrent = 3) => {
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [loadingImages, setLoadingImages] = useState(new Set());
  const [loadingQueue, setLoadingQueue] = useState([]);

  const preloadImage = useCallback((url) => {
    if (!url || loadedImages.has(url) || loadingImages.has(url)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, url]));
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(url);
          return newSet;
        });
        resolve(url);
      };

      img.onerror = () => {
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(url);
          return newSet;
        });
        reject(new Error(`Failed to load image: ${url}`));
      };

      setLoadingImages(prev => new Set([...prev, url]));
      img.src = url;
    });
  }, [loadedImages, loadingImages]);

  // Process loading queue
  useEffect(() => {
    const processQueue = async () => {
      if (loadingQueue.length === 0 || loadingImages.size >= maxConcurrent) {
        return;
      }

      const nextUrl = loadingQueue.shift();
      if (nextUrl) {
        try {
          await preloadImage(nextUrl);
        } catch (error) {
          console.warn('Image preload failed:', error);
        }
        
        // Process next item in queue
        setTimeout(processQueue, 100);
      }
    };

    processQueue();
  }, [loadingQueue, loadingImages.size, maxConcurrent, preloadImage]);

  // Add images to queue
  useEffect(() => {
    imageUrls.forEach(url => {
      if (!loadedImages.has(url) && !loadingImages.has(url) && !loadingQueue.includes(url)) {
        setLoadingQueue(prev => [...prev, url]);
      }
    });
  }, [imageUrls, loadedImages, loadingImages, loadingQueue]);

  return { loadedImages, loadingImages, preloadImage };
};

/**
 * Hook for optimizing video loading
 * @param {string} videoUrl - Video URL
 * @param {Object} options - Video options
 * @returns {Object} - { isVideoReady, videoRef, loadVideo }
 */
export const useVideoOptimization = (videoUrl, options = {}) => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const videoRef = useRef(null);

  const loadVideo = useCallback(() => {
    if (!videoRef.current || !videoUrl) return;

    const video = videoRef.current;
    
    // Reset states
    setIsVideoReady(false);
    setVideoError(null);

    // Set video source
    video.src = videoUrl;

    // Add event listeners
    const handleCanPlay = () => {
      setIsVideoReady(true);
    };

    const handleError = (e) => {
      console.error('Video loading error:', e);
      setVideoError(e.target.error);
    };

    const handleLoadedMetadata = () => {
      // Video metadata loaded, ready to play
      setIsVideoReady(true);
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);

    // Cleanup
    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoUrl]);

  return { isVideoReady, videoError, videoRef, loadVideo };
};
